import { produce } from 'immer';
import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  ImageAPI,
  classMap,
} from 'omni-ui';
import { Om2Dropdown, om2alert } from 'omni-campaign-ui';

import '../campaign-wysiwyg.js';
import { capitalizeWord, bytesToMb } from '../../helpers/utils.js';
import { MAX_IMAGE_BYTE_SIZE } from '../../helpers/constants.js';

OmniStyleElement.register();
Om2Dropdown.register();

/**
 * @description edit card module used by {@link CampaignExplorationViz} to either create a new Exploration Card or edit an existing one
 * @extends OmniElement
 */
export class ExplorationEditCard extends OmniElement {
  /**
   * @summary - module reactive elements
   * @property {Object} internalState - object containing module's state
   * @property {string} internalState.type - selected card type
   * @property {string} internalState.title
   * @property {Object|null} internalState.notes - WYSIWYG content object, if set
   * @property {Object<string, string>[]|File[]} internalState.images - images
   * @property {Boolean} isUploading - Boolean used to track if images are currently being sent to ImageAPI
   * @property {Array||null} newImgFiles - array of img files returned by omni-img-input
   * @property {Object} context - campaign context object
   */
  static get properties() {
    return {
      internalState: { type: Object },
      isUploading: { type: Boolean },
      newImgFiles: { type: Array },
      context: { type: Object },
    };
  }

  /**
   * Exploration Edit Card styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        /* position absolutely because ExplorationEditCard is not in same dom hierarchy as the display card  */
        :host {
          position: absolute;
          z-index: 2;
          bottom: 0;
        }

        .omni label.label {
          text-transform: none;
        }

        omni-tile.edit-card {
          width: 28.714rem;
          box-shadow:
            0 1.714rem 2.214rem -0.429rem rgba(1, 33, 78, 0.37),
            0 0.143rem 0.643rem -0.071rem rgba(1, 33, 78, 0.1);
          border-radius: 0.714rem;
        }

        omni-tile.edit-card::part(body-slot) {
          height: max-content;
          display: flex;
          flex-flow: column nowrap;
          padding: 2.143rem 1.714rem;
        }

        .dropdown-wrapper {
          margin-bottom: 2.143rem;
        }

        .input-wrapper {
          margin-bottom: 2.143rem;
        }

        .input-wrapper input {
          height: 2.571rem;
        }

        input::placeholder {
          background-color: unset;
          color: rgb(var(--rgb-core-dark));
          opacity: 0.3;
          border: none;
          font-size: 1rem;
          padding: 0.679rem;
        }

        .wysiwyg-wrapper {
          height: 12.286rem;
          margin-bottom: 2.143rem;
          /* for some reason campaign-wysiwyg margin bottom behaves odd */
        }
        .wysiwyg-wrapper campaign-wysiwyg {
          height: 12.286rem;
        }

        .img-upload-wrapper {
          /* for some reason campaign-wysiwyg margin bottom behaves odd */
          margin-top: 2.143rem;
          max-height: 8rem;
        }

        omni-img-input::part(filenames-container) {
          flex-grow: 1;
          overflow: auto;
          max-height: 5.857rem;
        }

        .btns-wrapper {
          gap: 0.715rem;
          margin-top: 2.143rem;
        }
      `,
    ];
  }

  /**
   * @summary initializes modules default properties
   * @property {String} ANsid - session id needed for ImageAPI
   * @property {Array} explorationTypes - options for drop down
   */
  constructor() {
    super();
    this.ANsid = window?.AN?.sso?.session?.sid;
    this.explorationTypes = [];
    this.internalState = {
      type: '',
      title: '',
      notes: null,
      images: [],
    };
    this.isUploading = false;
    this.newImgFiles = null;
  }

  /**
   * @summary Sets {@link internalState} to {@link explorationCard} if it is passed in or defaults it and adds new entries and initiates a new ImageAPI
   */
  connectedCallback() {
    super.connectedCallback();
    if (Object.keys(this.explorationCard).length) {
      this.internalState = this.explorationCard;
    } else {
      this.internalState = produce(this.internalState, draft => {
        draft.x = this.gridCoordinates?.x;
        draft.y = this.gridCoordinates?.y;
        draft.w = 1;
        draft.h = 1;
      });
    }
    this.imageAPI = new ImageAPI(this.ANsid, this.env);
  }

  /**
   *
   * @param {String} key - object key that needs to get updated
   * @param {*} value - new value that updates object key
   * @summary - updates the internalState when there is a change in data
   */
  _handleChange = (key, value) => {
    this.internalState = produce(this.internalState, draft => {
      draft[key] = value;
    });
  };

  /**
   * @param {OmniImgUpload~event:change} e - invoked on omni-img-upload change event. Takes the picture file object from the event's detail and sends it to the ImageAPI to and returns an image URL string to be used
   */
  _loadImages = async () => {
    // if no new images were uploaded, we return to avoid overwriting existing images
    if (this.newImgFiles === null) return;
    try {
      this.isUploading = true;
      if (!this.ANsid) throw new Error("Missing ANsid: can't upload images");
      const urlArray = await Promise.all(
        this.newImgFiles.map(async file => {
          const existsInInternalState = this.internalState.images.find(
            image => image.title === file.name,
          );
          // avoid submitting existing images in internal state to avoid duplicate uploads to the backend.
          const url = existsInInternalState
            ? existsInInternalState.url
            : await this.imageAPI.createImageURL(file);
          const title =
            file.name.substr(0, file.name.lastIndexOf('.')) || file.name;
          return { title, url };
        }),
      );
      this.internalState = produce(this.internalState, draft => {
        draft.images = urlArray;
      });
      this.isUploading = false;
    } catch (e2) {
      let errorMsg;
      if (e2.message) {
        errorMsg = e2.message;
      } else {
        try {
          const error = await e2.json();
          errorMsg = error.message;
        } catch {
          errorMsg = 'Image upload failed';
        }
      }
      this.isUploading = false;
      throw new Error(errorMsg);
    }
  };

  /**
   * @summary dispatches @event persona-card-close
   */
  _handleClose() {
    this.dispatchNewEvent('edit-card-close');
  }

  /**
   * @summary dispatches @event persona-card-submit
   */
  _handleSubmit = async () => {
    try {
      await this._loadImages();
      this.dispatchNewEvent('edit-card-submit', {
        detail: this.internalState,
      });
    } catch (e) {
      om2alert(e, {
        closeTimeout: 3000,
      });
      console.error(e); // eslint-disable-line no-console
    }
  };

  /**
   *
   * @returns {TemplateResult} main HTML to be rendered onto the DOM
   */
  render() {
    const canSubmit =
      // text fields
      this.internalState.type.trim() ||
      this.internalState.title.trim() ||
      this.internalState.notes !== null ||
      // images
      this.internalState.images.length ||
      this.newImgFiles?.length;

    return html`
    <omni-style>
      <omni-tile
        class="edit-card"
      >
        <div class="dropdown-wrapper">
          <label class='label is-size-7'>Exploration type</label>
          <om2-dropdown
            placeholder="Select type"
            .options=${this.explorationTypes.map(type => ({
              label: capitalizeWord(type),
              value: type,
            }))}
            .value=${this.internalState?.type}
            @change=${e => {
              const { value } = e.detail;
              this._handleChange('type', value);
            }}
          ></om2-dropdown>
        </div>

        <div class="input-wrapper is-flex is-flex-direction-column is-flex-wrap-nowrap">
          <label class="label is-size-7">Card title</label>
          <input
            placeholder="Enter title"
            class="input"
            type="text"
            .value=${this.internalState?.title}
            @input=${e => {
              const { value } = e.target;
              this._handleChange('title', value);
            }}
            ></input>
        </div>
        <div class="wysiwyg-wrapper">
          <label class="label is-size-7">Card notes</label>
          <campaign-assist-field
            fieldId=${this.internalState?.type}
            .fields=${this.context?.assistFields}
          >
            <campaign-wysiwyg
              @change=${({ detail: { value } }) => {
                this._handleChange(
                  'notes',
                  typeof value === 'string' && !value?.trim() ? null : value,
                );
              }}
              .initialContent=${this.internalState?.notes}
            ></campaign-wysiwyg>
        </campaign-assist-field>
        </div>
        <div class="img-upload-wrapper is-flex is-flex-direction-column is-flex-wrap-nowrap">
            <label class="is-size-7"> Upload image</label>
            <omni-img-input
              .env=${this.env}
              multiple
              previewable
              maxSize=${MAX_IMAGE_BYTE_SIZE}
              @change=${({ detail: { files } }) => {
                if (!files.length && this.internalState.images.length) {
                  this.internalState = produce(this.internalState, draft => {
                    draft.images = [];
                  });
                }
                this.newImgFiles = files?.length ? files : null;
              }}
              .previewSrc=${
                this.newImgFiles
                  ? ''
                  : this.internalState.images.map(img => img.url.trim())
              }
              .filename=${
                this.newImgFiles
                  ? ''
                  : this.internalState.images.map(img => img.title.trim())
              }
            >
              <p class='img-input-help' slot=help>Max size ${bytesToMb(
                MAX_IMAGE_BYTE_SIZE,
              )}MB</p>
            </omni-img-input>
        </div>
        <div class="btns-wrapper is-flex is-justify-content-end">
          <button
            class="button is-text"
            @click=${this._handleClose}
          >
            Cancel
          </button>
          <button
          class=${classMap({
            button: true,
            'is-primary': true,
            'is-loading': this.isUploading,
          })}
            @click=${this._handleSubmit}
            ?disabled=${!canSubmit}
          >
            Update
          </button>
        </div>
      </omni-tile>
    </omni-style>
    `;
  }
}

OmniElement.register('exploration-edit-card', ExplorationEditCard);
